import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { swiper, swiperSlide } from 'vue-awesome-swiper';

import { PreConfiguredMeal } from '@/models/pre-configured-meal';
import { SwiperOptions } from 'swiper';
import { ui } from '@/constants';
import { CustomerProfile, TagType, MealType } from '@/models';
import { some } from 'lodash';
import { isCustomerCanadian } from '@/helpers';
import { HomePageLinkInteractions } from '@/services/analytics/platforms/adobe-analytics/constants';
import { getFeaturedMealsInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';

interface SwiperComponent {
  swiper: {
    params: SwiperOptions;
  };
}

@Component({
  name: 'CmgFeatured',
  components: {
    swiper,
    swiperSlide,
  },
})
export default class CmgFeatured extends Vue {
  @Prop({ type: Array, required: false })
  preConfiguredMeals?: PreConfiguredMeal[];

  get header(): string {
    return this.isRestaurantSelected ? this.$appStore.state.cms.featuredPCE.restaurantSelectedHeader : this.$appStore.state.cms.featuredPCE.header;
  }

  get subHeader(): string {
    return this.$appStore.state.cms.featuredPCE.subHeader;
  }

  get isRestaurantSelected(): boolean {
    return !!this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get isLessThan900(): boolean {
    return this.$appStore.state.ui.clientWidth <= 900;
  }

  get featuredBackgroundImageUrl(): string {
    return this.$appStore.state.cms.images.backgrounds.ingredientsPattern.default;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isCustomerCanadian(): boolean | null {
    return isCustomerCanadian(this.customer, this.$appStore.state);
  }

  get cardWidth(): number {
    if (this.isRestaurantSelected) {
      return this.$appStore.state.ui.clientWidth > 1023 ? 270 : 355;
    }
    // 570px is the max width for large universal cards
    return this.$appStore.state.ui.clientWidth > 1199 ? 570 : 272;
  }

  get cardsOverflow(): boolean {
    if (this.filteredMeals.length > 4) {
      return true;
    }

    if (this.filteredMeals.length <= 1) {
      return false;
    }

    const outerMargin = 30;
    const cardViewMax = 1200;
    if (Math.min(this.$appStore.state.ui.clientWidth, cardViewMax) < (this.cardWidth + outerMargin) * this.filteredMeals.length) {
      return true;
    }

    return false;
  }

  get cardsOverflowUniversal(): boolean {
    if (this.filteredMeals.length <= 1) {
      return false;
    }

    const outerMargin = 24;
    if (this.$appStore.state.ui.clientWidth < (this.cardWidth + outerMargin) * this.filteredMeals.length) {
      return true;
    }

    return false;
  }

  get meals(): PreConfiguredMeal[] {
    return (this.preConfiguredMeals && this.preConfiguredMeals.length) ? this.preConfiguredMeals : this.$appStore.state.menu.preConfiguredMeals.filter(meal => meal.mealType == MealType.Influencer);
  }

  get filteredMeals(): PreConfiguredMeal[] {
    if (this.isEnrolledInLoyalty) {
      return this.meals;
    } else {
      return this.meals.filter(meal => !this.isExclusive(meal));
    }
  }

  isExclusive(mealData: PreConfiguredMeal): boolean {
    return some(mealData.mealTags, ['type', TagType.Rewards]);
  }

  mealPrimaryImage(mealData: PreConfiguredMeal): string | undefined {
    const image = mealData.primaryImages.find(img => img.imageCategory == 'WEB');
    return image && image.imageUrl;
  }

  mealSecondaryImage(mealData: PreConfiguredMeal): string {
    return mealData.secondaryImages?.[0]?.imageUrl;
  }

  showFindAChipotleModal(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.analytics.addLinkClickedEventToStore(getFeaturedMealsInteraction(HomePageLinkInteractions.FeaturedMealsFindAChipotle));
    this.$services.ui.setModalVisibility('findAChipotle', true);
    this.$services.ui.firstFocus();
  }

  lastWindowDimensions = {
    width: 1200,
    height: 0,
  };

  universalPCESwiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 0,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  };

  swiperOptions: SwiperOptions = {
    loopFillGroupWithBlank: true,
    slidesPerView: 'auto',
    spaceBetween: 0,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  };

  onMakeItMyOwn(mealName: string, itemPosition: number): void {
    this.$services.utils.setIsMakeItMyOwn(true);
    this.$services.analytics.addLinkClickedEventToStore(getFeaturedMealsInteraction(HomePageLinkInteractions.FeaturedMealsMakeItMyOwn, itemPosition, mealName));
  }

  onAddToBag(mealName: string, itemPosition: number): void {
    this.$services.analytics.addLinkClickedEventToStore(getFeaturedMealsInteraction(HomePageLinkInteractions.FeaturedMealsAddToBag, itemPosition, mealName));
  }
}
